import React, { memo, useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import SlideoverModal from "../SlideoverModal";
import Lucide from "src/base-components/Lucide";
import Button from "src/base-components/Button";
import TestImage from "../../assets/images/TestImages/user.png";
// import whatsappIcon from "../../assets/images/icon/ChatIcon.png";
import { PubNubAppProvider, ChatParticipant, CHANNEL_PREFIXES, generateOneToOneChannel, usePubNubContext } from "src/pubnub";
import { usePubNub } from "pubnub-react";
import OneToOneChatPanel from "./component/OneToOneChatPanel";
import NewChatDropdown from "./component/NewChatDropdown";
import CreateGroupModal, { GroupCreationData } from "./component/CreateGroupModal";
import GroupChatPanel from "./component/GroupChatPanel";
import { RootState, useAppDispatch } from "src/stores";
import { checkPermission } from "src/helpers";
import { chatAPIUrls } from "src/common/apiActions";
import {
  fetchChatUsersService,
  createChatRoomService,
  fetchChatRoomsService,
  readChatService,
} from "src/services/user.services";
import { setAlert } from "src/stores/basicSlices";
import { setChatCount } from "src/stores/messages/slice";
import chatIcon from "../../assets/images/chatIcons/CHAT.gif";
// import "../../assets/css/chatIonAnimated/chatAnimition.css"

/* ---- Chat user type from API (chat-users endpoint) ---- */
export interface ChatUser {
  id: number;
  name: string;
  last_name: string | null;
  email: string;
  user_type: string;
  extension_number: number | null;
  agent_extension: number | null;
}

/* ---- Chat room from group-chat API ---- */
interface ChatRoomUser {
  id: number;
  name: string;
  left_at: string | null;
}

interface ChatRoomLastMessage {
  id: number;
  message: string;
  sender_id: number;
  created_at: string;
}

interface ChatRoomFromAPI {
  id: number;
  chat_type: "private" | "group";
  group_name: string | null;
  channel_id: string;
  users: ChatRoomUser[];
  last_message: ChatRoomLastMessage | null;
  unread_count: number;
}

// Chat thread type (1:1 private)
interface ChatThread {
  id: string | number;
  name: string;
  avatar: string;
  message: string;
  time: string;
  rawTime: number;   // timestamp ms — used for sorting (most recent first)
  unread: number;
  channelId: string;
}

/* ---- Group thread type ---- */
interface GroupThread {
  id: string;
  name: string;
  channelId: string;
  members: ChatParticipant[];
  message: string;
  time: string;
  rawTime: number;   // timestamp ms — used for sorting (most recent first)
  unread: number;
  isRemoved?: boolean; // true when current user was removed from the group
}

/* ---- Ongoing thread type (combined private + group for ongoing tab) ---- */
interface OngoingThread {
  id: string | number;
  name: string;
  avatar?: string;
  message: string;
  time: string;
  rawTime: number;
  unread: number;
  channelId: string;
  _type: "chat" | "group";
  members?: ChatParticipant[];
  isRemoved?: boolean;
  userId?: string;
}

/* ---- Inner component that has access to PubNub context ---- */
function ChatInner({
  isOpen,
  onClose,
  currentUserId,
  currentUserName,
  chatUsers,
  chatUsersHasMore,
  chatUsersLoading,
  onLoadMoreChatUsers,
  onSearchChatUsers,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  currentUserName: string;
  chatUsers: ChatUser[];
  chatUsersHasMore: boolean;
  chatUsersLoading: boolean;
  onLoadMoreChatUsers: () => void;
  onSearchChatUsers: (query: string) => void;
}) {
  const pubnub = usePubNub();
  const { isTokenReady } = usePubNubContext();

  // Check if the logged-in user has permission to create chat groups
  const canCreateGroup = checkPermission(chatAPIUrls?.createChatRoom.slice(1));

  // Check if the logged-in user has permission to view ongoing chats (admin/manager read-only view)
  const canViewOngoingChats = checkPermission(chatAPIUrls?.ongoingChat.slice(1));


  // Add these state variables for chat rooms pagination
  const [chatRoomsPage, setChatRoomsPage] = useState(1);
  const [chatRoomsHasMore, setChatRoomsHasMore] = useState(true);
  const [chatRoomsLoading, setChatRoomsLoading] = useState(false);
  const [chatRoomsSearchInput, setChatRoomsSearchInput] = useState(""); // For input field
  const [chatRoomsSearchTerm, setChatRoomsSearchTerm] = useState(""); // For actual API call
  const chatRoomsSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getPrivateChatName = (users: ChatRoomUser[]) => {
    const other = users.find((u) => String(u.id) !== currentUserId);
    return other?.name || "Unknown";
  };

  const getPrivateChatUserId = (users: ChatRoomUser[]) => {
    const other = users.find((u) => String(u.id) !== currentUserId);
    return other ? String(other.id) : "";
  };

  const [activeTab, setActiveTab] = useState<"groups" | "chats" | "ongoing">("chats");

  // Map tab name → API chat_type param
  const getChatTypeForTab = (tab: "groups" | "chats" | "ongoing"): "private" | "group" | "ongoing" =>
    tab === "groups" ? "group" : tab === "ongoing" ? "ongoing" : "private";
  const [selectedThread, setSelectedThread] = useState<any>(null);
  const [oneToOneChatUser, setOneToOneChatUser] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // 1:1 chat threads — from API
  const [
    chatThreads, setChatThreads] = useState<ChatThread[]>([]);

  // Group threads — from API
  const [groupThreads, setGroupThreads] = useState<GroupThread[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupThread | null>(null);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [roomId, setRoomId] = useState<string>('');
  const [ongoingSelectedType, setOngoingSelectedType] = useState<"chat" | "group" | null>(null);

  const dispatch = useAppDispatch();

  // Ongoing threads — combined private + group for "Ongoing Chats" tab
  const [ongoingThreads, setOngoingThreads] = useState<OngoingThread[]>([]);

  // const getDateLabel = (date: string | Date): string => {
  //   // Parse ISO string → local time automatically
  //   const localDate = new Date(date);

  //   const isSameDay = (a: Date, b: Date) =>
  //     a.getFullYear() === b.getFullYear() &&
  //     a.getMonth() === b.getMonth() &&
  //     a.getDate() === b.getDate();

  //   const today = new Date();

  //   // Today — show local time only
  //   if (isSameDay(localDate, today)) {
  //     return localDate.toLocaleTimeString([], {
  //       hour: "2-digit",
  //       minute: "2-digit",
  //       hour12: true,
  //     });
  //   }

  //   // Derive yesterday from today
  //   const yesterday = new Date(today);
  //   yesterday.setDate(today.getDate() - 1);

  //   if (isSameDay(localDate, yesterday)) {
  //     return "Yesterday";
  //   }

  //   // Same year — show month + day
  //   if (localDate.getFullYear() === today.getFullYear()) {
  //     return localDate.toLocaleDateString([], {
  //       month: "short",
  //       day: "numeric",
  //     });
  //   }

  //   // Older — show month + day + year
  //   return localDate.toLocaleDateString([], {
  //     month: "short",
  //     day: "numeric",
  //     year: "numeric",
  //   });
  // };

  const getDateLabel = (date: string | Date): string => {
    const localDate = new Date(date);

    const tz = "America/Edmonton"; // Canada/Mountain

    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const today = new Date();

    if (isSameDay(localDate, today)) {
      return localDate.toLocaleTimeString("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      });
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(localDate, yesterday)) {
      return "Yesterday";
    }

    if (localDate.getFullYear() === today.getFullYear()) {
      return localDate.toLocaleDateString("en-CA", {
        month: "short",
        day: "numeric",
        timeZone: tz,
      });
    }

    return localDate.toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    });
  };

  // Build a preview string from a PubNub message for sidebar display
  const buildMessagePreview = (msg: any, isGroup: boolean = false): string => {
    let preview = msg?.text?.slice(0, 40) || "";
    if (msg?.type === "file" || msg?.file || msg?.files?.length) {
      const fileType = (msg?.file || msg?.files?.[0])?.type || "";
      if (fileType.startsWith("image/")) preview = "📷 Photo";
      else if (fileType.startsWith("video/")) preview = "🎥 Video";
      else preview = "📎 File";
      if (msg?.text?.trim()) preview += ` • ${msg.text.slice(0, 30)}`;
    }
    if (isGroup && msg?.senderName) {
      preview = `${msg.senderName}: ${preview}`;
    }
    return preview;
  };

  // Helper: map API rooms to ChatThread[]
  const buildChatThreads = (rooms: ChatRoomFromAPI[]): ChatThread[] =>
    rooms.map(room => ({
      id: getPrivateChatUserId(room.users),
      name: getPrivateChatName(room.users),
      avatar: TestImage,
      message: "",
      time: "",
      rawTime: 0,
      unread: room.unread_count || 0,
      channelId: room.channel_id,
      userId: getPrivateChatUserId(room.users),
    }));

  // Helper: map API rooms to GroupThread[]
  const buildGroupThreads = (rooms: ChatRoomFromAPI[]): GroupThread[] =>
    rooms.map(room => {
      const currentUserEntry = room.users.find(u => String(u.id) === currentUserId);
      return {
        id: String(room.id),
        name: room.group_name || "Unnamed Group",
        channelId: room.channel_id,
        members: room.users.map(u => ({ id: String(u.id), name: u.name, leftAt: u.left_at })),
        message: "",
        time: "",
        rawTime: 0,
        unread: room.unread_count || 0,
        isRemoved: !!currentUserEntry?.left_at,
      };
    });

  // Helper: map API rooms to OngoingThread[] (combined private + group)
  const buildOngoingThreads = (rooms: ChatRoomFromAPI[]): OngoingThread[] =>
    rooms.map(room => {
      const isGroup = room.chat_type === "group";
      const currentUserEntry = isGroup ? room.users.find(u => String(u.id) === currentUserId) : undefined;
      return {
        id: isGroup ? String(room.id) : getPrivateChatUserId(room.users),
        name: isGroup
          ? (room.group_name || "Unnamed Group")
          : room.users.map(u => u.name).join(" & "),
        avatar: TestImage,
        message: "",
        time: "",
        rawTime: 0,
        unread: room.unread_count || 0,
        channelId: room.channel_id,
        _type: isGroup ? "group" : "chat",
        members: isGroup ? room.users.map(u => ({ id: String(u.id), name: u.name, leftAt: u.left_at })) : undefined,
        userId: isGroup ? undefined : getPrivateChatUserId(room.users),
        isRemoved: isGroup ? !!currentUserEntry?.left_at : undefined,
      };
    });

  const fetchChatRooms = useCallback(async (
    page: number = 1,
    reset: boolean = false,
    search: string = "",
    chatType: "private" | "group" | "ongoing" = "private"
  ) => {
    try {
      setChatRoomsLoading(true);
      const response = await fetchChatRoomsService(search, page, chatType);

      // Check if response was successful
      if (!response.success) {
        console.error("[Chat] API returned unsuccessful response");
        return;
      }

      // Get data and pagination from response
      const rooms: ChatRoomFromAPI[] = response.data || [];
      const pagination = response.pagination || {
        current_page: page,
        last_page: 1,
        total: 0,
        per_page: 20
      };

      // Build threads based on chatType
      let threads: ChatThread[] = [];
      let groups: GroupThread[] = [];
      let ongoing: OngoingThread[] = [];

      if (chatType === "private") {
        threads = buildChatThreads(rooms);
      } else if (chatType === "group") {
        groups = buildGroupThreads(rooms);
      } else {
        // ongoing — API returns both private + group
        ongoing = buildOngoingThreads(rooms);
      }

      // Always fetch last message from PubNub for ALL channels (single source of truth)
      const allChannels = rooms.map(room => room.channel_id);

      if (allChannels.length > 0) {
        try {
          const pubnubRes = await pubnub.fetchMessages({
            channels: allChannels,
            count: 1,
          });

          // Generic helper to update last message from PubNub
          const updateLastMsg = (item: { channelId: string; message: string; rawTime: number; time: string }, isGroup: boolean) => {
            const msgs = pubnubRes.channels?.[item.channelId];
            if (!msgs?.length) return;
            const lastMsg = msgs[msgs.length - 1];
            item.message = buildMessagePreview(lastMsg.message, isGroup);
            item.rawTime = Math.floor(Number(lastMsg.timetoken) / 10000);
            // item.time = getDateLabel(new Date(item.rawTime).toISOString());
            item.time = getDateLabel(new Date(item.rawTime));
          };

          if (chatType === "private") {
            threads.forEach(t => updateLastMsg(t, false));
          } else if (chatType === "group") {
            groups.forEach(g => {
              if (g.isRemoved) return; // removed member → keep message empty
              updateLastMsg(g, true);
            });
          } else {
            ongoing.forEach(o => {
              if (o.isRemoved) return; // removed member → keep message empty
              updateLastMsg(o, o._type === "group");
            });
          }
        } catch (err) {
          console.warn("[Chat] Failed to fetch last messages from PubNub:", err);
        }
      }

      // Sort by last message time (most recent conversation first)
      threads.sort((a, b) => b.rawTime - a.rawTime);
      groups.sort((a, b) => b.rawTime - a.rawTime);
      ongoing.sort((a, b) => b.rawTime - a.rawTime);

      // Update the correct state based on chatType
      if (chatType === "private") {
        if (reset) {
          setChatThreads(threads);
        } else {
          setChatThreads(prev => {
            const existingIds = new Set(prev.map(t => t.id));
            return [...prev, ...threads.filter(t => !existingIds.has(t.id))];
          });
        }
      } else if (chatType === "group") {
        if (reset) {
          // Use functional update to merge with any groups added by real-time system messages
          // that arrived while the API request was in flight (prevents duplicates & lost entries)
          setGroupThreads(prev => {
            const apiGroupMap = new Map(groups.map(g => [g.id, g]));
            // Keep system-message-added groups that aren't in the API response yet
            const preserved = prev.filter(g => !apiGroupMap.has(g.id));
            return [...groups, ...preserved].sort((a, b) => b.rawTime - a.rawTime);
          });
        } else {
          setGroupThreads(prev => {
            const existingIds = new Set(prev.map(g => g.id));
            return [...prev, ...groups.filter(g => !existingIds.has(g.id))];
          });
        }
      } else {
        if (reset) {
          setOngoingThreads(ongoing);
        } else {
          setOngoingThreads(prev => {
            const existingIds = new Set(prev.map(o => `${o._type}-${o.id}`));
            return [...prev, ...ongoing.filter(o => !existingIds.has(`${o._type}-${o.id}`))];
          });
        }
      }

      // Update pagination state
      setChatRoomsPage(pagination.current_page);
      setChatRoomsHasMore(pagination.current_page < pagination.last_page);

    } catch (err) {
      console.error("[Chat] Failed to fetch chat rooms:", err);
    } finally {
      setChatRoomsLoading(false);
    }
  }, [currentUserId, pubnub]);

  // Fetch rooms when modal opens / reset when modal closes
  useEffect(() => {
    if (isOpen) {
      // Full reset + fresh fetch when modal opens
      setChatThreads([]);
      setGroupThreads([]);
      setOngoingThreads([]);
      setThreads([]);
      setSelectedThread(null);
      setSelectedGroup(null);
      setOneToOneChatUser(null);
      setOngoingSelectedType(null);
      setActiveTab("chats");
      setChatRoomsPage(1);
      setChatRoomsHasMore(true);
      setChatRoomsSearchInput("");
      setChatRoomsSearchTerm("");
      setRoomId('');
    } else {
      // Reset UI state when modal closes — keep chatThreads & groupThreads
      // alive so PubNub subscriptions stay active and unread counts keep updating
      setOngoingThreads([]);
      setThreads([]);
      setSelectedThread(null);
      setSelectedGroup(null);
      setOneToOneChatUser(null);
      setOngoingSelectedType(null);
      setActiveTab("chats");
      setChatRoomsPage(1);
      setChatRoomsHasMore(true);
      setChatRoomsSearchInput("");
      setChatRoomsSearchTerm("");
      setRoomId('');
    }
  }, [isOpen]);

  // Fetch chat rooms once modal is open AND token is ready
  useEffect(() => {
    if (isOpen && isTokenReady) {
      fetchChatRooms(1, true, "", "private");
      fetchChatRooms(1, true, "", "group");
    }
  }, [isOpen, isTokenReady]);

  // Fetch both private & group rooms once on mount (for initial badge count)
  useEffect(() => {
    if (isTokenReady && !isOpen) {
      fetchChatRooms(1, true, "", "private");
      fetchChatRooms(1, true, "", "group");
    }
  }, [isTokenReady]);


  // Handle search input changes — call fetchChatRooms directly (no useEffect trigger)
  const handleChatRoomsSearchInput = useCallback((query: string) => {
    setChatRoomsSearchInput(query);

    if (chatRoomsSearchTimerRef.current) {
      clearTimeout(chatRoomsSearchTimerRef.current);
    }

    chatRoomsSearchTimerRef.current = setTimeout(() => {
      setChatRoomsSearchTerm(query);
      setChatRoomsPage(1);
      fetchChatRooms(1, true, query, getChatTypeForTab(activeTab));
    }, 400);
  }, [fetchChatRooms, activeTab]);


  // Handle scroll pagination — call fetchChatRooms directly
  const handleChatRoomsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (chatRoomsHasMore && !chatRoomsLoading) {
        const nextPage = chatRoomsPage + 1;
        setChatRoomsPage(nextPage);
        fetchChatRooms(nextPage, false, chatRoomsSearchTerm, getChatTypeForTab(activeTab));
      }
    }
  }, [chatRoomsHasMore, chatRoomsLoading, chatRoomsPage, chatRoomsSearchTerm, fetchChatRooms, activeTab]);
  // Refs so listeners always see the latest selected state (avoids stale closures)
  const selectedGroupRef = useRef(selectedGroup);
  const oneToOneChatUserRef = useRef(oneToOneChatUser);
  useEffect(() => { selectedGroupRef.current = selectedGroup; }, [selectedGroup]);
  useEffect(() => { oneToOneChatUserRef.current = oneToOneChatUser; }, [oneToOneChatUser]);

  // Track delivery receipts sent from background listeners to avoid duplicates
  const bgDeliveryReceiptsRef = useRef<Set<string>>(new Set());

  // ──────────────────────────────────────────────────────────
  //  SYSTEM CHANNEL — listen for group-creation broadcasts
  // ──────────────────────────────────────────────────────────
  const systemChannel = `chat-system-${currentUserId}`;

  useEffect(() => {
    if (!isTokenReady) return;
    const listener = {
      message: (event: any) => {
        if (event.channel !== systemChannel) return;
        const payload = event.message;
        // --- group-created event ---
        if (payload?.type === "group-created") {
          const incomingGroup: GroupThread = { ...payload.group, rawTime: payload.group.rawTime || Date.now() };
          setGroupThreads((prev) => {
            if (prev.some((g) => g.id === incomingGroup.id)) return prev;
            return [incomingGroup, ...prev];
          });
        }

        // --- private-room-created event (other user sees the new 1:1 chat) ---
        if (payload?.type === "private-room-created") {
          const incomingThread: ChatThread = {
            ...payload.thread,
            avatar: TestImage,
            rawTime: payload.thread.rawTime || Date.now(),
          };
          setChatThreads((prev) => {
            if (prev.some((t) => t.id === incomingThread.id)) return prev;
            return [incomingThread, ...prev];
          });
        }

        // --- group-message event (sidebar preview only — unread is handled by
        //     the background channel listener to avoid double-counting) ---
        if (payload?.type === "group-message") {
          const { groupId, text, senderName } = payload;
          setGroupThreads((prev) =>
            prev.map((g) =>
              g.id === groupId && !g.isRemoved
                ? {
                  ...g,
                  message: `${senderName}: ${text.length > 40 ? text.slice(0, 40) + "..." : text}`,
                  // time: getDateLabel(new Date().toISOString()),
                  time: getDateLabel(new Date()),
                  rawTime: Date.now(),
                }
                : g,
            ),
          );
        }

        // --- member-added event (I was added to an existing group) ---
        if (payload?.type === "member-added") {
          const { groupId: gId, channelId: chId, groupName: gName, members: gMembers } = payload;
          // Ensure leftAt is cleared for all members (especially the re-added user)
          const cleanMembers = (gMembers || []).map((m: ChatParticipant) => ({ ...m, leftAt: null }));
          setGroupThreads((prev) => {
            // Check both id AND channelId to prevent duplicates
            if (prev.some((g) => g.id === gId || g.channelId === chId)) {
              // Already in list — update members & reset isRemoved (re-added case)
              return prev.map((g) => (g.id === gId || g.channelId === chId) ? { ...g, members: cleanMembers, isRemoved: false, name: gName || g.name } : g);
            }
            // New group for this user
            return [{
              id: gId,
              name: gName,
              channelId: chId,
              members: cleanMembers,
              message: `${payload.addedBy} added you`,
              // time: getDateLabel(new Date().toISOString()),
              time: getDateLabel(new Date()),
              rawTime: Date.now(),
              unread: 0,
            }, ...prev];
          });
          // If currently viewing this group, reset isRemoved on selectedGroup too
          if (selectedGroupRef.current?.id === gId) {
            setSelectedGroup((prev) =>
              prev ? { ...prev, members: cleanMembers, isRemoved: false } : prev
            );
          }
        }

        // --- member-removed event (I was removed from a group) ---
        // WhatsApp style: keep the group visible but mark as removed (read-only)
        if (payload?.type === "member-removed") {
          const { groupId: gId, channelId: chId, members: updatedMembers, removedAt: payloadRemovedAt } = payload;
          const removedAt = payloadRemovedAt || new Date().toISOString();
          // Build members list that includes the current user with leftAt set,
          // so the GroupChatPanel can filter messages after removal time.
          const membersWithSelf = [
            ...(updatedMembers || []),
            { id: currentUserId, name: currentUserName, leftAt: removedAt },
          ];
          setGroupThreads((prev) =>
            prev.map((g) =>
              (g.id === gId || g.channelId === chId)
                ? {
                  ...g,
                  isRemoved: true,
                  unread: 0,
                  message: "",
                  members: membersWithSelf,
                }
                : g
            ),
          );
          // Update selectedGroup so the panel re-renders with read-only state + updated members
          if (selectedGroupRef.current?.id === gId || selectedGroupRef.current?.channelId === chId) {
            setSelectedGroup((prev) =>
              prev
                ? {
                  ...prev,
                  isRemoved: true,
                  members: membersWithSelf,
                }
                : prev
            );
          }
        }

        // --- member-list-updated event (a member was removed by admin, update my member list) ---
        if (payload?.type === "member-list-updated") {
          const { groupId: gId, channelId: chId, members: updatedMembers } = payload;
          if (updatedMembers) {
            setGroupThreads((prev) =>
              prev.map((g) =>
                (g.id === gId || g.channelId === chId) ? { ...g, members: updatedMembers } : g
              ),
            );
            // If currently viewing this group, update selectedGroup so online status refreshes
            if (selectedGroupRef.current?.id === gId || selectedGroupRef.current?.channelId === chId) {
              setSelectedGroup((prev) =>
                prev ? { ...prev, members: updatedMembers } : prev
              );
            }
          }
        }

        // --- group-renamed event (another user renamed the group) ---
        if (payload?.type === "group-renamed") {
          const { groupId: gId, newName } = payload;
          setGroupThreads((prev) =>
            prev.map((g) => g.id === gId ? { ...g, name: newName } : g)
          );
          setSelectedGroup((prev) => {
            if (!prev || prev.id !== gId) return prev;
            return { ...prev, name: newName } as GroupThread;
          });
        }
      },
    };

    pubnub.addListener(listener);
    pubnub.subscribe({ channels: [systemChannel] });

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [systemChannel] });
    };
  }, [pubnub, systemChannel, isTokenReady]);

  // ──────────────────────────────────────────────────────────
  //  BACKGROUND SUBSCRIPTIONS — subscribe to ALL group
  //  channels so real-time messages / presence work even
  //  when the group isn't actively open.
  // ──────────────────────────────────────────────────────────
  const subscribedGroupsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isTokenReady) return;
    const currentChannels = new Set(
      groupThreads.map((g) => g.channelId),
    );

    // channels to add
    const toSubscribe: string[] = [];
    currentChannels.forEach((ch) => {
      if (!subscribedGroupsRef.current.has(ch)) {
        toSubscribe.push(ch);
        subscribedGroupsRef.current.add(ch);
      }
    });

    // channels to remove (group was deleted)
    const toUnsubscribe: string[] = [];
    subscribedGroupsRef.current.forEach((ch) => {
      if (!currentChannels.has(ch)) {
        toUnsubscribe.push(ch);
        subscribedGroupsRef.current.delete(ch);
      }
    });

    if (toSubscribe.length > 0) {
      pubnub.subscribe({ channels: toSubscribe, withPresence: true });
    }
    if (toUnsubscribe.length > 0) {
      pubnub.unsubscribe({ channels: toUnsubscribe });
    }
  }, [pubnub, groupThreads, isTokenReady]);

  // ──────────────────────────────────────────────────────────
  //  Listen for messages on ALL group channels for sidebar
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    const listener = {
      message: (event: any) => {
        // Only handle group channels
        if (!event.channel.startsWith(CHANNEL_PREFIXES.GROUP)) return;

        const msgData = event.message;

        // ── Send delivery receipt from background if chat panel isn't open ──
        if (
          msgData?.senderId &&
          msgData.senderId !== currentUserId &&
          event.timetoken
        ) {
          const isOpen = selectedGroupRef.current?.channelId === event.channel;
          const receiptKey = `delivered-${event.timetoken}`;
          if (!isOpen && !bgDeliveryReceiptsRef.current.has(receiptKey)) {
            bgDeliveryReceiptsRef.current.add(receiptKey);
            pubnub
              .addMessageAction({
                channel: event.channel,
                messageTimetoken: String(event.timetoken),
                action: { type: "receipt", value: "delivered" },
              })
              .catch((err: any) => {
                if (err?.status?.statusCode !== 409) {
                  bgDeliveryReceiptsRef.current.delete(receiptKey);
                }
              });
          }
        }

        setGroupThreads((prev) => {
          const index = prev.findIndex(g => g.channelId === event.channel);
          if (index === -1) return prev;

          // Skip message update for removed groups
          if (prev[index].isRemoved) return prev;

          const msgData = event.message;
          const isMine = msgData?.senderId === currentUserId;
          const isOpen = selectedGroupRef.current?.id === prev[index].id;

          const updatedGroup = {
            ...prev[index],
            message: buildMessagePreview(msgData, true),
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: isMine || isOpen
              ? prev[index].unread
              : prev[index].unread + 1,
          };

          const newGroups = [...prev];
          newGroups.splice(index, 1);
          newGroups.unshift(updatedGroup);

          return newGroups;
        });

        // Also update ongoing threads in real-time
        setOngoingThreads((prev) => {
          const index = prev.findIndex(t => t.channelId === event.channel);
          if (index === -1) return prev;

          const msgData = event.message;
          const isMine = msgData?.senderId === currentUserId;

          const updated = {
            ...prev[index],
            message: buildMessagePreview(msgData, prev[index]._type === "group"),
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: isMine ? prev[index].unread : prev[index].unread + 1,
          };

          const newList = [...prev];
          newList.splice(index, 1);
          newList.unshift(updated);
          return newList;
        });

      },
    };

    pubnub.addListener(listener);
    return () => pubnub.removeListener(listener);
  }, [pubnub]);

  // ──────────────────────────────────────────────────────────
  //  BACKGROUND SUBSCRIPTIONS — 1:1 chat channels
  //  Subscribe to all 1:1 channels so presence stays alive
  //  and sidebar can show last message + unread count.
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isTokenReady) return;
    const oneToOneChannels = chatThreads.map((t) => t.channelId);
    if (oneToOneChannels.length === 0) return;

    pubnub.subscribe({ channels: oneToOneChannels, withPresence: true });

    return () => {
      pubnub.unsubscribe({ channels: oneToOneChannels });
    };
  }, [pubnub, chatThreads, isTokenReady]);

  // ──────────────────────────────────────────────────────────
  //  Listen for messages on ALL 1:1 channels for sidebar
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    const listener = {
      message: (event: any) => {
        // Only handle 1:1 channels
        if (!event.channel.startsWith(CHANNEL_PREFIXES.ONE_TO_ONE)) return;

        const msgData = event.message;
        const isMine = msgData?.senderId === currentUserId;

        // ── Send delivery receipt from background if chat panel isn't open ──
        if (
          msgData?.senderId &&
          !isMine &&
          event.timetoken
        ) {
          const isOpen = oneToOneChatUserRef.current != null &&
            event.channel.includes(oneToOneChatUserRef.current.id);
          const receiptKey = `delivered-${event.timetoken}`;
          if (!isOpen && !bgDeliveryReceiptsRef.current.has(receiptKey)) {
            bgDeliveryReceiptsRef.current.add(receiptKey);
            pubnub
              .addMessageAction({
                channel: event.channel,
                messageTimetoken: String(event.timetoken),
                action: { type: "receipt", value: "delivered" },
              })
              .catch((err: any) => {
                if (err?.status?.statusCode !== 409) {
                  bgDeliveryReceiptsRef.current.delete(receiptKey);
                }
              });
          }
        }

        setChatThreads((prev) => {
          const index = prev.findIndex(t => t.channelId === event.channel);
          if (index === -1) return prev;

          const msgData = event.message;
          const isMine = msgData?.senderId === currentUserId;
          const isCurrentlyOpen = oneToOneChatUserRef.current?.id === prev[index].id;

          const updatedThread = {
            ...prev[index],
            message: buildMessagePreview(msgData),
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: isMine || isCurrentlyOpen
              ? prev[index].unread
              : prev[index].unread + 1,
          };

          const newThreads = [...prev];
          newThreads.splice(index, 1);   // remove old position
          newThreads.unshift(updatedThread); // move to top

          return newThreads;
        });

        // Also update ongoing threads in real-time
        setOngoingThreads((prev) => {
          const index = prev.findIndex(t => t.channelId === event.channel);
          if (index === -1) return prev;

          const isMine = msgData?.senderId === currentUserId;

          const updated = {
            ...prev[index],
            message: buildMessagePreview(msgData, prev[index]._type === "group"),
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: isMine ? prev[index].unread : prev[index].unread + 1,
          };

          const newList = [...prev];
          newList.splice(index, 1);
          newList.unshift(updated);
          return newList;
        });

      },
    };

    pubnub.addListener(listener);
    return () => pubnub.removeListener(listener);
  }, [pubnub]);

  // ──────────────────────────────────────────────────────────
  //  Clear unread when selecting a group
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedGroup) return;
    setGroupThreads((prev) =>
      prev.map((g) => (g.id === selectedGroup.id ? { ...g, unread: 0 } : g)),
    );
    // Also clear unread on the matching ongoing thread
    setOngoingThreads((prev) =>
      prev.map((t) => (t._type === "group" && t.id === selectedGroup.id ? { ...t, unread: 0 } : t)),
    );
  }, [selectedGroup?.id]);

  // Clear unread when selecting a 1:1 chat
  useEffect(() => {
    if (!oneToOneChatUser) return;
    setChatThreads((prev) =>
      prev.map((t) => (t.id === oneToOneChatUser.id ? { ...t, unread: 0 } : t)),
    );
    // Also clear unread on the matching ongoing thread
    setOngoingThreads((prev) =>
      prev.map((t) => (t._type === "chat" && t.id === oneToOneChatUser.id ? { ...t, unread: 0 } : t)),
    );
  }, [oneToOneChatUser?.id]);

  // const threads = activeTab === "groups"
  //   ? groupThreads
  //   : activeTab === "ongoing"
  //     ? ongoingThreads
  //     : chatThreads;

  const [threads, setThreads] = useState<any[]>([]);

  useEffect(() => {
    // setSelectedThread(null);
    // setSelectedGroup(null);
    // setOneToOneChatUser(null);
    // setOngoingSelectedType(null);
    if (activeTab === "groups") {
      setThreads(groupThreads);
    }
    else if (activeTab === "ongoing") {
      setThreads(ongoingThreads);
    }
    else {
      setThreads(chatThreads);
    }
  }, [activeTab, groupThreads, ongoingThreads, chatThreads])

  // Total unread counts for tab badges
  const totalChatsUnread = chatThreads.reduce((sum, t) => sum + t.unread, 0);
  const totalGroupsUnread = groupThreads.reduce((sum, g) => sum + g.unread, 0);

  // Sync total unread count to Redux so the floating icon badge stays updated
  const totalUnread = totalChatsUnread + totalGroupsUnread;
  useEffect(() => {
    dispatch(setChatCount({ chatCount: totalUnread }));
  }, [totalUnread, dispatch]);

  // Handle selecting a chat thread
  const handleSelectChatThread = (thread: any) => {
    setSelectedThread(thread);
    setRoomId(thread?.id);

    // Mark chat as read via API & reset unread locally
    if (thread?.channelId && thread.unread > 0) {

      // Reset unread count in the correct list
      if (activeTab === "chats" || (activeTab === "ongoing" && thread._type === "chat")) {
        setChatThreads((prev) =>
          prev.map((t) => (t.channelId === thread.channelId ? { ...t, unread: 0 } : t))
        );
      }
      if (activeTab === "groups" || (activeTab === "ongoing" && thread._type === "group")) {
        setGroupThreads((prev) =>
          prev.map((g) => (g.channelId === thread.channelId ? { ...g, unread: 0 } : g))
        );
      }
      if(activeTab !== "ongoing"){
        readChatService(thread.channelId).catch((err: any) =>
          console.warn("[Chat] Failed to mark chat as read:", err)
        );
      }
      setOngoingThreads((prev) =>
        prev.map((t) => (t.channelId === thread.channelId ? { ...t, unread: 0 } : t))
      );
    }

    if (activeTab === "ongoing") {
      // Ongoing tab — _type comes from OngoingThread data
      const isGroup = thread._type === "group";
      if (isGroup) {
        setOngoingSelectedType("group");
        setOneToOneChatUser(null);
        // Build selectedGroup from the ongoing thread's data (it has members)
        setSelectedGroup({
          id: thread.id,
          name: thread.name,
          channelId: thread.channelId,
          members: thread.members || [],
          message: thread.message,
          time: thread.time,
          rawTime: thread.rawTime,
          unread: thread.unread,
          isRemoved: thread.isRemoved,
        });
      } else {
        setOngoingSelectedType("chat");
        setOneToOneChatUser({ id: thread?.userId || thread?.id, name: thread.name });
        setSelectedGroup(null);
      }
    } else if (activeTab === "chats") {
      setOneToOneChatUser({ id: thread?.id, name: thread.name });
      setSelectedGroup(null);
      setOngoingSelectedType(null);
    } else {
      setOneToOneChatUser(null);
      const g = groupThreads.find((gt) => gt.id === thread.id);
      setSelectedGroup(g || null);
      setOngoingSelectedType(null);
    }
  };

  // Handle new chat from dropdown — create a private room via API
  const handleNewChat = useCallback(
    async (user: { id: string; name: string }) => {
      setActiveTab("chats");
      setOneToOneChatUser(user);
      setSelectedThread(null);
      setSelectedGroup(null);

      // Check if a thread already exists for this user
      const existing = chatThreads.find((t) => t.id === user.id);
      if (existing) {
        setSelectedThread(existing);
        return;
      }

      // Create a private chat room via API
      const channelId = generateOneToOneChannel(currentUserId, user.id);
      try {
        const response = await createChatRoomService({
          channel_id: channelId,
          chat_type: "private",
          group_name: null,
          user_ids: [Number(user.id)],
        });

        // Build the new thread object for optimistic UI
        if (response.success && response.data) {
          const newThread: ChatThread = {
            id: user.id,
            name: user.name,
            avatar: TestImage,
            message: "",
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: 0,
            channelId: response.data.channel_id || channelId,
          };


          // Add to own list immediately
          setChatThreads((prev) => {
            if (prev.some((t) => t.id === user.id)) return prev;
            return [newThread, ...prev];
          });
          setSelectedThread(newThread);
        }

        // Notify the other user so their sidebar updates in real-time
        const otherUserSystemChannel = `chat-system-${user.id}`;
        pubnub
          .publish({
            channel: otherUserSystemChannel,
            message: {
              type: "private-room-created",
              thread: {
                id: currentUserId,
                name: currentUserName,
                avatar: "",
                message: "",
                // time: getDateLabel(new Date().toISOString()),
                time: getDateLabel(new Date()),
                rawTime: Date.now(),
                unread: 0,
                channelId,
              },
            },
            storeInHistory: true,
          })
          .catch((err) => console.warn("[System] Failed to notify private room:", err));

      } catch (err) {
        console.error("[Chat] Failed to create private room:", err);
      }
    },
    [chatThreads, currentUserId, currentUserName, pubnub, fetchChatRooms, chatRoomsSearchTerm],
  );

  // ──────────────────────────────────────────────────────────
  //  Handle creating a new group — save via API + broadcast
  // ──────────────────────────────────────────────────────────
  const handleCreateGroup = useCallback(
    async (data: GroupCreationData) => {

      if (data?.name.length > 100) {
        dispatch(setAlert({
          text: 'The group name is too long. Please keep it under 100 characters.', alertType: "Swal",
          swalIcon: "error",
        }));
      }
      const groupId = `grp-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const channelId = `chat-group-${groupId}`;
      const memberIds = data.members.map((m) => Number(m.id));

      // Create group room via API
      try {
        const response = await createChatRoomService({
          channel_id: channelId,
          chat_type: "group",
          group_name: data.name,
          user_ids: memberIds,
        });

        if (response.success && response.data) {
          const newGroup: GroupThread = {
            id: groupId,
            name: data.name,
            channelId,
            members: data.members,
            message: "Group created",
            // time: getDateLabel(new Date().toISOString()),
            time: getDateLabel(new Date()),
            rawTime: Date.now(),
            unread: 0,
          };

          // Add to own list immediately for instant UI feedback
          setGroupThreads((prev) => [newGroup, ...prev]);
          setIsCreateGroupOpen(false);
          setActiveTab("groups");
          setSelectedThread(newGroup);
          setSelectedGroup(newGroup);
          setOneToOneChatUser(null);

          // Broadcast group-created event to every OTHER member's system channel
          data.members.forEach((member) => {
            if (member.id === currentUserId) return;
            const memberSystemChannel = `chat-system-${member.id}`;
            pubnub
              .publish({
                channel: memberSystemChannel,
                message: { type: "group-created", group: newGroup },
                storeInHistory: true,
              })
              .then(() => console.log(`[System] Group broadcast sent to ${member.id}`))
              .catch((err) => console.warn(`[System] Failed to notify ${member.id}:`, err));
          });

          // Publish a system message inside the group channel
          pubnub
            .publish({
              channel: channelId,
              message: {
                id: `sys-${Date.now()}`,
                text: `${currentUserName} created group "${data.name}"`,
                senderId: currentUserId,
                senderName: currentUserName,
                timestamp: Date.now(),
                channelId,
                type: "system" as const,
              },
              storeInHistory: true,
            })
        }

      } catch (err) {
        console.error("[Chat] Failed to create group room:", err);
      }
    },
    [pubnub, currentUserId, currentUserName, fetchChatRooms, chatRoomsSearchTerm],
  );

  return (
    <SlideoverModal
      size="xl"
      visible={isOpen}
      className="p-0 overflow-hidden"
      onClose={onClose}
      headerLeft={
        <div className="flex items-center gap-2">
          {/* <img src={whatsappIcon} className="w-8 h-8" /> */}
          {/* <Lucide icon="MessageSquare" className="w-8 h-8" /> */}
          <img src={chatIcon} className="w-12 h-12" alt="Chat Icon" />

          <h2 className="text-[18px] text-[#1D293D] font-bold">TravoTera Connect</h2>
        </div>
      }
      headerRight={
        activeTab === "ongoing" ? null :
          activeTab === "groups" ? (
            canCreateGroup ? (
              <Button
                variant="outline-primary"
                onClick={() => setIsCreateGroupOpen(true)}
                className="flex items-center gap-1 h-[31px] border border-[#C2CDDB]"
              >
                <Lucide icon="Users" className="w-4 h-4" />
                Create Group
              </Button>
            ) : null
          ) : (
            <NewChatDropdown
              onSelectUser={handleNewChat}
              chatUsers={chatUsers}
              currentUserId={currentUserId}
              loading={chatUsersLoading}
              hasMore={chatUsersHasMore}
              onLoadMore={onLoadMoreChatUsers}
              onSearch={onSearchChatUsers}
            />
          )
      }
    >
      <div className="flex h-full overflow-hidden">
        {/* ================= LEFT SIDEBAR ================= */}
        <div className="w-[22rem] flex-shrink-0 border-r bg-white flex flex-col">
          {/* Tabs */}
          <div className="p-3">
            <div className="flex bg-[#ECECF0] rounded-full p-1">
              <button
                onClick={() => {
                  if(activeTab === "groups"){
                    return;
                  }
                  setActiveTab("groups");
                  setOneToOneChatUser(null);
                  setSelectedThread(null);
                  setSelectedGroup(null);
                  setOngoingSelectedType(null);
                  setChatRoomsSearchInput("");
                  setChatRoomsSearchTerm("");
                  setThreads([]);
                  if (groupThreads.length === 0) {
                    setChatRoomsPage(1);
                    fetchChatRooms(1, true, "", "group");
                  }
                }}
                className={`flex-1 rounded-full text-sm py-1 transition relative ${activeTab === "groups"
                  ? "bg-white font-medium shadow"
                  : "text-slate-500 "
                  }`}
              >
                Groups
                {totalGroupsUnread > 0 && (
                  <span className="absolute -top-1 -right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)] text-white text-[10px] flex items-center justify-center">
                    {totalGroupsUnread > 99 ? "99+" : totalGroupsUnread}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  if(activeTab === "chats"){
                    return;
                  }
                  setActiveTab("chats");
                  setSelectedThread(null);
                  setSelectedGroup(null);
                  setOneToOneChatUser(null);
                  setOngoingSelectedType(null);
                  setChatRoomsSearchInput("");
                  setChatRoomsSearchTerm("");
                  setThreads([]);
                  if (chatThreads.length === 0) {
                    setChatRoomsPage(1);
                    fetchChatRooms(1, true, "", "private");
                  }
                }}
                className={`flex-1 rounded-full text-sm py-1 transition relative ${activeTab === "chats"
                  ? "bg-white font-medium shadow"
                  : "text-slate-500 "
                  }`}
              >
                Chats
                {totalChatsUnread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)] text-white text-[10px] flex items-center justify-center">
                    {totalChatsUnread > 99 ? "99+" : totalChatsUnread}
                  </span>
                )}
              </button>

              {canViewOngoingChats && (
                <button
                  onClick={() => {
                    if(activeTab === "ongoing"){
                      return;
                    }
                    setActiveTab("ongoing");
                    setSelectedThread(null);
                    setSelectedGroup(null);
                    setOneToOneChatUser(null);
                    setOngoingSelectedType(null);
                    setChatRoomsSearchInput("");
                    setChatRoomsSearchTerm("");
                    setThreads([]);
                    if (ongoingThreads.length === 0) {
                      setChatRoomsPage(1);
                      fetchChatRooms(1, true, "", "ongoing");
                    }
                  }}
                  className={`flex-1 rounded-full text-sm py-1 transition relative ${activeTab === "ongoing"
                    ? "bg-white font-medium shadow"
                    : "text-slate-500 "
                    }`}
                >
                  Ongoing
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="px-3 pb-3">
            <div className="relative">
              <Lucide
                icon="Search"
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                placeholder="Search"
                value={chatRoomsSearchInput}
                onChange={(e) => handleChatRoomsSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-0 focus:border-slate-200"
              />

              {chatRoomsSearchInput && (
                <button
                  onClick={() => {
                    setChatRoomsSearchInput("");
                    setChatRoomsSearchTerm("");
                    setChatRoomsPage(1);
                    fetchChatRooms(1, true, "", getChatTypeForTab(activeTab));
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <Lucide icon="X" className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto px-2 space-y-2" onScroll={handleChatRoomsScroll}>
            {/* Loading state */}
            {!isTokenReady || (chatRoomsLoading && threads.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Lucide icon="Loader" className="w-8 h-8 animate-spin mb-2" />
                <p className="text-sm">Loading conversations...</p>
              </div>
            ) : threads.length === 0 && activeTab === "groups" && !chatRoomsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Lucide icon="Users" className="w-10 h-10 mb-2" />
                <p className="text-sm">No groups yet</p>
                <p className="text-xs mt-1">Create one to start chatting</p>
              </div>
            ) : threads.length === 0 && activeTab === "ongoing" && !chatRoomsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Lucide icon="Eye" className="w-10 h-10 mb-2" />
                <p className="text-sm">No ongoing conversations</p>
                <p className="text-xs mt-1">All active chats will appear here</p>
              </div>
            ) : (
              threads.map((thread) => (
                <div
                  key={`${(thread as any)._type || activeTab}-${thread.id}`}
                  onClick={() => handleSelectChatThread(thread)}
                  className={`flex gap-3 items-start p-3 rounded-xl cursor-pointer transition ${selectedThread?.channelId === thread.channelId
                    ? "bg-[#F1F5FF] border border-blue-200"
                    : "bg-[#F8F8F8]"
                    }`}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
<div className="w-10 h-10 rounded-full bg-[linear-gradient(135deg,#0F3460_0%,#0A2342_100%)] flex items-center justify-center text-white font-bold text-sm">
                      {thread.name.charAt(0).toUpperCase()}
                    </div>
                    {activeTab === "ongoing" && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                        <Lucide
                          icon={(thread as any)._type === "group" ? "Users" : "User"}
                          className="w-2.5 h-2.5 text-purple-600"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div >
                      <div className="flex justify-between items-center">
                        <h4 className="text-sm font-medium">{thread.name.length > 22 ? thread.name.slice(0, 23) + "..." : thread.name}</h4>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {thread.time}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {thread?.message.length > 35 ? thread?.message?.slice(0, 36) + "..." : thread?.message}
                      </p>
                      {thread.unread > 0 && (
                        <span className="min-w-[17px] h-[18px] text-[10px]  px-1 rounded-full bg-[linear-gradient(135deg,#0F3460_0%,#0A2342_100%)] text-white flex items-center justify-center">
                          {thread.unread}
                        </span>
                      )}

                    </div>
                  </div>
                </div>
              ))


            )}

            {chatRoomsLoading && threads.length > 0 && (
              <div className="flex justify-center py-4">
                <Lucide icon="Loader" className="w-5 h-5 animate-spin text-purple-600" />
              </div>
            )}
          </div>
        </div>

        {/* ================= RIGHT CHAT ================= */}
        {activeTab === "chats" && oneToOneChatUser ? (
          /* One-to-One Chat with PubNub */
          <OneToOneChatPanel
            currentRoomId={roomId}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            targetUserId={oneToOneChatUser.id}
            targetUserName={oneToOneChatUser.name}
            onBack={() => setOneToOneChatUser(null)}
          />
        ) : activeTab === "groups" && selectedGroup ? (
          /* Group Chat with PubNub */
          <GroupChatPanel
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            groupId={selectedGroup.id}
            groupName={selectedGroup.name}
            members={selectedGroup.members}
            channelId={selectedGroup.channelId}
            onBack={() => setSelectedGroup(null)}
            availableUsers={chatUsers}
            canManageGroup={!!canCreateGroup}
            isRemoved={!!selectedGroup.isRemoved}
            onMembersChange={(newMembers) => {
              setGroupThreads((prev) =>
                prev.map((g) =>
                  g.id === selectedGroup!.id ? { ...g, members: newMembers } : g
                )
              );


              setSelectedGroup((prev) =>
                prev ? { ...prev, members: newMembers } : prev
              );
            }}
            onGroupNameChange={(newName) => {
              setGroupThreads((prev) =>
                prev.map((g) =>
                  g.id === selectedGroup!.id ? { ...g, name: newName } : g
                )
              );
              setSelectedGroup((prev) =>
                prev ? { ...prev, name: newName } : prev
              );
            }}
          />
        ) : activeTab === "ongoing" && ongoingSelectedType === "chat" && oneToOneChatUser ? (
          /* Ongoing tab — read-only 1:1 Chat */
          <OneToOneChatPanel
            currentRoomId={roomId}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            targetUserId={oneToOneChatUser.id}
            targetUserName={oneToOneChatUser.name}
            channelId={selectedThread?.channelId}
            onBack={() => { setOneToOneChatUser(null); setOngoingSelectedType(null); }}
            readOnly
          />
        ) : activeTab === "ongoing" && ongoingSelectedType === "group" && selectedGroup ? (
          /* Ongoing tab — read-only Group Chat */
          <GroupChatPanel
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            groupId={selectedGroup.id}
            groupName={selectedGroup.name}
            members={selectedGroup.members}
            channelId={selectedGroup.channelId}
            onBack={() => { setSelectedGroup(null); setOngoingSelectedType(null); }}
            availableUsers={chatUsers}
            canManageGroup={!!canCreateGroup}
            isRemoved={!!selectedGroup.isRemoved}
            readOnly
          />
        ) : (
          /* Empty placeholder — plain background */
          <div className="flex flex-col flex-1 bg-primary/10 items-center justify-center">
            <Lucide icon="MessageCircle" className="w-16 h-16 text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {canCreateGroup && (
        <CreateGroupModal
          isOpen={isCreateGroupOpen}
          onClose={() => setIsCreateGroupOpen(false)}
          onCreateGroup={handleCreateGroup}
          chatUsers={chatUsers}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          loading={chatUsersLoading}
          hasMore={chatUsersHasMore}
          onLoadMore={onLoadMoreChatUsers}
          onSearch={onSearchChatUsers}
        />
      )}
    </SlideoverModal>
  );
}

/* ---- Outer wrapper — provides PubNub context ---- */
function WhatsappChatsStatic({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  // Get current logged-in user from Redux
  const authUser = useSelector((state: RootState) => state.authUser);
  const currentUserId = String(authUser?.id || "");
  const currentUserName = `${authUser?.name || ""}${authUser?.last_name ? " " + authUser.last_name : ""}`.trim();

  const [chatUsers, setChatUsers] = useState<ChatUser[]>([]);
  const [chatUsersPage, setChatUsersPage] = useState(1);
  const [chatUsersHasMore, setChatUsersHasMore] = useState(true);
  const [chatUsersLoading, setChatUsersLoading] = useState(false);
  const [chatUsersSearch, setChatUsersSearch] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return ()=>{
      setChatUsers([])
    }
  },[])

  // Fetch chat users (paginated, with optional search)
  const loadChatUsers = async (page: number, reset = false, search = "") => {
    if (chatUsersLoading) return;
    setChatUsersLoading(true);
    try {
      const res: any = await fetchChatUsersService(page, search);
      const pageData = res?.data || res || [];
      const users: ChatUser[] = Array.isArray(pageData) ? pageData : pageData?.data || [];

      // Handle paginated response
      const nextPageUrl = res?.next_page_url || pageData?.next_page_url || null;
      const currentPage = res?.current_page || pageData?.current_page || page;

      setChatUsers((prev) => reset ? users : [...prev, ...users]);
      setChatUsersPage(currentPage);
      setChatUsersHasMore(!!nextPageUrl);
    } catch (err) {
      console.error("[Chat] Failed to fetch users:", err);
    } finally {
      setChatUsersLoading(false);
    }
  };

  // Load first page when chat opens
  useEffect(() => {
    if (!isOpen) return;
    setChatUsers([]);
    setChatUsersPage(1);
    setChatUsersHasMore(true);
    setChatUsersSearch("");
    loadChatUsers(1, true, "");
  }, [isOpen]);

  // Load more handler (respects current search)
  const handleLoadMoreChatUsers = useCallback(() => {
    if (chatUsersHasMore && !chatUsersLoading) {
      loadChatUsers(chatUsersPage + 1, false, chatUsersSearch);
    }
  }, [chatUsersHasMore, chatUsersLoading, chatUsersPage, chatUsersSearch, loadChatUsers]);

  // Debounced search handler
  const handleSearchChatUsers = useCallback((query: string) => {
    const trimmedQuery = query.trim();
    setChatUsersSearch(trimmedQuery);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setChatUsers([]);
      setChatUsersPage(1);
      setChatUsersHasMore(true);
      loadChatUsers(1, true, trimmedQuery);
    }, 400);
  }, [chatUsersSearch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  if (!currentUserId) return null;

  return (
    <PubNubAppProvider userId={currentUserId}>
      <ChatInner
        isOpen={isOpen}
        onClose={onClose}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        chatUsers={chatUsers}
        chatUsersHasMore={chatUsersHasMore}
        chatUsersLoading={chatUsersLoading}
        onLoadMoreChatUsers={handleLoadMoreChatUsers}
        onSearchChatUsers={handleSearchChatUsers}
      />
    </PubNubAppProvider>
  );
}

export default WhatsappChatsStatic;
